import * as React from 'react';

import {
  Button,
  DialogContent,
  DialogHeader,
  DialogOverlay,
} from '@rematter/ui-web';
import { LucideAlertTriangle, LucideTrash2 } from 'lucide-react';

import { useTranslations } from '../../i18n/useTranslations';

type AccountDeleteConfirmDialogProps = {
  isOpen: boolean;
  accountCount?: number;
  accountName?: string;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
};

//
// ⚛️ ⚛️ ⚛️ ACCOUNT DELETE CONFIRM DIALOG ⚛️ ⚛️ ⚛️
//
export const AccountDeleteConfirmDialog: React.FC<
  AccountDeleteConfirmDialogProps
> = ({ isOpen, accountCount, accountName, onConfirm, onCancel }) => {
  const t = useTranslations();
  const [isLoading, setIsLoading] = React.useState(false);

  const handleConfirm = React.useCallback(async () => {
    setIsLoading(true);
    try {
      await onConfirm();
    } finally {
      setIsLoading(false);
    }
  }, [onConfirm]);

  if (!isOpen) return null;

  const title = accountName
    ? t.accountsView.deleteDialog.titleSingle(accountName)
    : t.accountsView.deleteDialog.titleBatch(accountCount ?? 0);

  return (
    <DialogOverlay
      isOpen
      isDismissable
      onOpenChange={(open) => !open && onCancel()}
      className="@AccountDeleteConfirmDialog"
    >
      <DialogContent onOpenChange={(open: boolean) => !open && onCancel()}>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <LucideAlertTriangle className="size-5 text-warning" />
            <span className="text-base font-semibold">{title}</span>
          </div>
        </DialogHeader>
        <div className="flex flex-col gap-2 px-4">
          <div className="text-base text-neutral/50">
            {t.accountsView.deleteDialog.cascadeWarning}
          </div>
          <div className="flex justify-end gap-2 py-4">
            <Button
              variant="text"
              color="neutral"
              onPress={onCancel}
              isDisabled={isLoading}
            >
              {t.accountsView.dialog.actions.cancel}
            </Button>
            <Button
              variant="solid"
              color="failure"
              icon={<LucideTrash2 className="size-4" />}
              onPress={handleConfirm}
              isLoading={isLoading}
            >
              {t.accountsView.toolbar.delete}
            </Button>
          </div>
        </div>
      </DialogContent>
    </DialogOverlay>
  );
};
